(() => {
  const JSON_ASSET_URL_RAW = (() => {
    const fromCurrent =
      document.currentScript && document.currentScript.src
        ? document.currentScript.src
        : "";

    if (fromCurrent) {
      return fromCurrent.replace(/\/beads-custom\.js(?:\?.*)?$/i, "/ttb.json");
    }

    const fallbackScript = document.querySelector('script[src*="beads-custom.js"]');
    if (fallbackScript && fallbackScript.src) {
      return fallbackScript.src.replace(/\/beads-custom\.js(?:\?.*)?$/i, "/ttb.json");
    }

    const anyThemeAssetScript = document.querySelector('script[src*="/cdn/shop/t/"][src*="/assets/"]');
    if (anyThemeAssetScript && anyThemeAssetScript.src) {
      return anyThemeAssetScript.src.replace(/\/assets\/[^/?#]+(?:\?.*)?$/i, "/assets/ttb.json");
    }

    return "/assets/ttb.json";
  })();

  // Expected row shape: [width, rim, fullTireName, aspect, sku]
  const IDX_WIDTH = 0;
  const IDX_RIM = 1;
  const IDX_FULL = 2;
  const IDX_ASPECT = 3;
  const IDX_SKU = 4;

  // Ladder for +1 sizing in Bias mode
  const LADDER = [100, 150, 200, 250, 300, 400, 500, 600, 700];

  // Combo SKUs → multiple pills
  const SKU_COMBOS = {
    "A2Z-400": [{ sku: "A2Z-150", qty: 1 }, { sku: "A2Z-250", qty: 1 }],
    "A2Z-500": [{ sku: "A2Z-100", qty: 1 }, { sku: "A2Z-150", qty: 1 }, { sku: "A2Z-250", qty: 1 }],
    "A2Z-600": [{ sku: "A2Z-300", qty: 2 }],
    "A2Z-700": [{ sku: "A2Z-200", qty: 1 }, { sku: "A2Z-250", qty: 2 }]
  };

  // ---------- shared helpers ----------
  function uniqueSorted(values) {
    const uniq = Array.from(new Set(values));
    uniq.sort((a, b) => {
      const na = Number(a), nb = Number(b);
      const aNum = !Number.isNaN(na), bNum = !Number.isNaN(nb);
      if (aNum && bNum) return na - nb;
      return String(a).localeCompare(String(b));
    });
    return uniq;
  }

  function setSelectOptions(selectEl, values, placeholder) {
    selectEl.innerHTML = "";
    const opt0 = document.createElement("option");
    opt0.value = "";
    opt0.textContent = placeholder;
    selectEl.appendChild(opt0);

    values.forEach((v) => {
      const opt = document.createElement("option");
      opt.value = v;
      opt.textContent = v;
      selectEl.appendChild(opt);
    });
  }

  function normalizeSku(sku) {
    return String(sku || "").trim().toUpperCase();
  }

  function skuClassToken(sku) {
    return normalizeSku(sku).replace(/[^A-Z0-9_-]+/g, "-");
  }

  function skuDisplayLabel(sku) {
    const normalized = normalizeSku(sku);
    if (!normalized) return normalized;
    if (normalized.startsWith("A2Z-")) return normalized;
    return normalized.replace(/^[^-]+-/, "");
  }

  function shiftSkuUpOne(sku) {
    const s = normalizeSku(sku);
    const m = s.match(/^A2Z-(\d+)$/);
    if (!m) return s;

    const n = Number(m[1]);
    const idx = LADDER.indexOf(n);
    if (idx === -1) return s;

    const shifted = LADDER[Math.min(idx + 1, LADDER.length - 1)];
    return `A2Z-${shifted}`;
  }

  function makePill({ sku, qty = 1 }) {
    const pill = document.createElement("span");
    pill.className = `ttb__pill ttb__pill--${skuClassToken(sku)}`;
    pill.textContent = skuDisplayLabel(sku);

    if (qty > 1) {
      const q = document.createElement("span");
      q.className = "ttb__qty";
      q.textContent = `x${qty}`;
      pill.appendChild(q);
    }
    return pill;
  }

  // ---------- load JSON once ----------
  let rowsPromise = null;

  function toSameOriginAssetUrl(url) {
    const input = String(url || "").trim();
    if (!input) return input;

    try {
      const parsed = new URL(input, window.location.href);
      return `${parsed.pathname}${parsed.search}${parsed.hash}`;
    } catch (e) {
      return input;
    }
  }

  const JSON_ASSET_URL = toSameOriginAssetUrl(JSON_ASSET_URL_RAW);

  function loadRowsOnce() {
    if (rowsPromise) return rowsPromise;

    const jsonUrl = JSON_ASSET_URL;
    rowsPromise = fetch(jsonUrl)
      .then((res) => {
        if (!res.ok) throw new Error(`HTTP ${res.status} while fetching ${jsonUrl}`);
        return res.json();
      })
      .then((json) => {
        if (!Array.isArray(json)) throw new Error("ttb.json did not contain a top-level array.");
        const cleaned = json.filter((r) => Array.isArray(r) && r.length >= 5);
        if (!cleaned.length) throw new Error("ttb.json had no valid rows (expected arrays of length 5).");
        return cleaned;
      });

    return rowsPromise;
  }

  // ---------- init one widget ----------
  function initWidget(root, rows) {
    const rimSel = root.querySelector(".ttb-rim");
    const aspectSel = root.querySelector(".ttb-aspect");
    const widthSel = root.querySelector(".ttb-width");
    const titleEl = root.querySelector(".ttb-title");
    const pillEl = root.querySelector(".ttb-pill");
    const errorBox = root.querySelector(".ttb-error");
    const plySelect = root.querySelector(".ttb-ply");

    if (!rimSel || !aspectSel || !widthSel || !titleEl || !pillEl || !errorBox) {
      console.warn("[TTB Calc] Missing required elements inside widget", root);
      return;
    }

    // state per widget
    let plyMode = "radial";
    let lastResult = null;

    function showError(msg) {
      errorBox.textContent = msg;
      errorBox.style.display = "block";
    }
    function clearError() {
      errorBox.textContent = "";
      errorBox.style.display = "none";
    }
    function resetTitle() {
      titleEl.textContent = "Application Guide";
    }
    function setTitleFor(fullName) {
      const label = plyMode === "bias" ? "Bias Ply" : "Radial Ply";
      titleEl.textContent = `${fullName} (${label})`;
    }

    function setPillsForSku(baseSku) {
      pillEl.innerHTML = "";

      const base = normalizeSku(baseSku);
      if (!base) {
        const empty = document.createElement("span");
        empty.className = "ttb__pill ttb__pill--empty";
        empty.textContent = "-";
        pillEl.appendChild(empty);
        return;
      }

      const effectiveSku = (plyMode === "bias") ? shiftSkuUpOne(base) : base;
      const combo = SKU_COMBOS[effectiveSku];

      const items = combo
        ? combo.map(x => ({ sku: normalizeSku(x.sku), qty: x.qty || 1 }))
        : [{ sku: effectiveSku, qty: 1 }];

      items.forEach((item, index) => {
        if (index > 0) {
          const plus = document.createElement("span");
          plus.className = "ttb__plus";
          plus.textContent = "+";
          pillEl.appendChild(plus);
        }
        pillEl.appendChild(makePill(item));
      });
    }

    function hideResult() {
      resetTitle();
      setPillsForSku(null);
      lastResult = null;
    }

    function showResult(sku, fullName) {
      lastResult = { sku, fullName };
      setTitleFor(fullName);
      setPillsForSku(sku);
    }

    function rimsFromAllRows() {
      return uniqueSorted(rows.map((r) => r[IDX_RIM]));
    }
    function aspectsForRim(rim) {
      return uniqueSorted(rows.filter((r) => r[IDX_RIM] === rim).map((r) => r[IDX_ASPECT]));
    }
    function widthsForRimAndAspect(rim, aspect) {
      return uniqueSorted(rows.filter((r) => r[IDX_RIM] === rim && r[IDX_ASPECT] === aspect).map((r) => r[IDX_WIDTH]));
    }
    function findMatch(rim, aspect, width) {
      return rows.filter((r) => r[IDX_RIM] === rim && r[IDX_ASPECT] === aspect && r[IDX_WIDTH] === width);
    }

    function resetAspect() {
      aspectSel.value = "";
      aspectSel.disabled = true;
      setSelectOptions(aspectSel, [], "Aspect Ratio");
    }
    function resetWidth() {
      widthSel.value = "";
      widthSel.disabled = true;
      setSelectOptions(widthSel, [], "Tire Width");
    }

    // ply dropdown
    if (plySelect) {
      plySelect.value = "radial";
      plySelect.addEventListener("change", () => {
        plyMode = (plySelect.value === "bias") ? "bias" : "radial";
        if (lastResult) showResult(lastResult.sku, lastResult.fullName);
      });
    }

    // events
    rimSel.addEventListener("change", () => {
      clearError();
      hideResult();

      const rim = rimSel.value;
      resetAspect();
      resetWidth();
      if (!rim) return;

      const aspects = aspectsForRim(rim);
      if (!aspects.length) return showError("No aspect ratios found for that rim size.");

      setSelectOptions(aspectSel, aspects, "Aspect Ratio");
      aspectSel.disabled = false;
    });

    aspectSel.addEventListener("change", () => {
      clearError();
      hideResult();

      const rim = rimSel.value;
      const aspect = aspectSel.value;

      resetWidth();
      if (!rim || !aspect) return;

      const widths = widthsForRimAndAspect(rim, aspect);
      if (!widths.length) return showError("No tire widths found for that rim + aspect combo.");

      setSelectOptions(widthSel, widths, "Tire Width");
      widthSel.disabled = false;
    });

    widthSel.addEventListener("change", () => {
      clearError();
      hideResult();

      const rim = rimSel.value;
      const aspect = aspectSel.value;
      const width = widthSel.value;
      if (!rim || !aspect || !width) return;

      const matches = findMatch(rim, aspect, width);
      if (!matches.length) return showError("No match found for that combination.");

      const row = matches[0];
      showResult(row[IDX_SKU], row[IDX_FULL]);
    });

    // initial
    clearError();
    hideResult();
    resetAspect();
    resetWidth();
    setSelectOptions(rimSel, rimsFromAllRows(), "Rim Size");
    rimSel.disabled = false;
  }

  // ---------- boot ----------
  function boot() {
    const widgets = Array.from(document.querySelectorAll("[data-ttb]"));
    if (!widgets.length) return;

    loadRowsOnce()
      .then((rows) => widgets.forEach((w) => initWidget(w, rows)))
      .catch((err) => {
        console.error("[TTB Calc] Failed to load", err);
        widgets.forEach((w) => {
          const box = w.querySelector(".ttb-error");
          if (box) {
            box.textContent = "Could not load bead chart data. Check that ttb.json exists in assets and is valid JSON.";
            box.style.display = "block";
          }
        });
      });
  }

  if (document.readyState === "loading") {
    document.addEventListener("DOMContentLoaded", boot);
  } else {
    boot();
  }
})();
